import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Plug, Trash2, Github, GitlabIcon, Cloud, CheckCircle2 } from "lucide-react";

const PROVIDERS = [
  {
    id: "github", name: "GitHub", hint: "Personal Access Token", url: "https://github.com/settings/tokens",
    permissions: "Clássico: marque os scopes \"repo\" e \"workflow\" (sem workflow, o envio de pipeline pra .github/workflows/ falha). Fine-grained: dê acesso ao repositório e permissão \"Contents: Read and write\" + \"Workflows: Read and write\".",
  },
  {
    id: "gitlab", name: "GitLab", hint: "Personal Access Token", url: "https://gitlab.com/-/user_settings/personal_access_tokens",
    permissions: "Marque o scope \"api\" (acesso completo) — necessário pra listar repositórios, criar branch e commitar a pipeline.",
  },
  {
    id: "azure", name: "Azure DevOps", hint: "Personal Access Token", url: "https://dev.azure.com/_usersSettings/tokens",
    permissions: "Dê permissão \"Code: Read & Write\" (e \"Read\" em Project and Team se listar org/projeto falhar) — necessário pra listar repositórios, criar branch e commitar a pipeline. Se o PAT for restrito a uma organização específica (em vez de \"All accessible organizations\"), informe o nome dela abaixo.",
  },
];

const PROVIDER_ICONS = { github: Github, gitlab: GitlabIcon, azure: Cloud };
function ProviderIcon({ id, ...props }) {
  const Icon = PROVIDER_ICONS[id] || Cloud;
  return <Icon {...props} />;
}

export default function Connections() {
  const [conns, setConns] = useState([]);
  const [provider, setProvider] = useState("github");
  const [token, setToken] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/connections").then((r) => setConns(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const connect = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { provider, token };
      if (provider === "gitlab") payload.base_url = baseUrl || null;
      if (provider === "azure") payload.organization = baseUrl || null;
      await api.post("/connections", payload);
      toast.success(`${provider} conectado`);
      setToken("");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/connections/${id}`);
      toast.success("Conexão removida");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const isConnected = (pid) => conns.find((c) => c.provider === pid);
  const current = PROVIDERS.find((p) => p.id === provider);

  return (
    <div className="p-8 fadeup">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-1">Integrações</div>
      <h1 className="text-4xl font-black tracking-tighter mb-8">Conexões</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-line bg-surface p-6">
          <h2 className="text-lg font-bold tracking-tight mb-4 flex items-center gap-2"><Plug size={18} className="text-acid" /> Nova conexão</h2>
          <div className="flex gap-2 mb-4">
            {PROVIDERS.map((p) => (
              <button key={p.id} data-testid={`select-provider-${p.id}`} onClick={() => { setProvider(p.id); setBaseUrl(""); }}
                className={`flex-1 flex items-center justify-center gap-2 border px-3 py-3 text-sm uppercase tracking-widest transition-colors ${provider === p.id ? "border-acid text-acid bg-acid/5" : "border-line text-zinc-400 hover:border-zinc-500"}`}>
                <ProviderIcon id={p.id} size={16} /> {p.name}
              </button>
            ))}
          </div>
          <form onSubmit={connect} className="space-y-4">
            {provider === "gitlab" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-400">Server URL (opcional)</label>
                <input data-testid="connection-base-url" type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
                  className="w-full mt-1 bg-void border border-line px-3 py-3 text-sm focus:border-acid focus:outline-none focus:ring-1 focus:ring-acid" placeholder="https://gitlab.suaempresa.com" />
                <span className="text-xs text-zinc-400 mt-1 inline-block">Deixe em branco para usar gitlab.com. Preencha para instância self-hosted.</span>
              </div>
            )}
            {provider === "azure" && (
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-400">Organização (opcional)</label>
                <input data-testid="connection-organization" type="text" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
                  className="w-full mt-1 bg-void border border-line px-3 py-3 text-sm focus:border-acid focus:outline-none focus:ring-1 focus:ring-acid" placeholder="minha-org" />
                <span className="text-xs text-zinc-400 mt-1 inline-block">Deixe em branco se o PAT tem acesso a todas as organizações. Preencha se o PAT for restrito a uma organização.</span>
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-400">Access Token</label>
              <input data-testid="connection-token" type="password" required value={token} onChange={(e) => setToken(e.target.value)}
                className="w-full mt-1 bg-void border border-line px-3 py-3 text-sm focus:border-acid focus:outline-none focus:ring-1 focus:ring-acid" placeholder="ghp_... / glpat-... / azure PAT" />
              <a href={current.url} target="_blank" rel="noreferrer" className="text-xs text-zinc-400 hover:text-acid mt-1 inline-block">
                {current.hint} ↗
              </a>
              {current.permissions && (
                <div className="text-xs text-zinc-500 mt-1 border-l-2 border-line pl-2">{current.permissions}</div>
              )}
            </div>
            <button data-testid="connection-submit" disabled={loading}
              className="w-full bg-acid text-black font-bold uppercase tracking-widest text-sm py-3 hover:bg-[#32e612] transition-colors disabled:opacity-50">
              {loading ? "Validando..." : "Conectar"}
            </button>
          </form>
        </div>

        <div className="border border-line bg-surface p-6">
          <h2 className="text-lg font-bold tracking-tight mb-4">Conectadas</h2>
          {conns.length === 0 ? (
            <div className="text-sm text-zinc-400 border border-dashed border-line p-6 text-center">&gt; nenhuma conexão_</div>
          ) : (
            <div className="space-y-3">
              {conns.map((c) => (
                <div key={c.id} data-testid={`connection-${c.provider}`} className="flex items-center justify-between border border-line px-4 py-3">
                  <div className="flex items-center gap-3">
                    <ProviderIcon id={c.provider} size={18} className={c.provider === "gitlab" ? "text-orange-500" : ""} />
                    <div>
                      <div className="text-sm font-bold">{c.provider}</div>
                      <div className="text-xs text-zinc-400">@{c.username}{c.base_url ? ` · ${c.base_url}` : ""}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 size={16} className="text-acid" />
                    <button data-testid={`disconnect-${c.provider}`} onClick={() => remove(c.id)} className="text-zinc-400 hover:text-destructive transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
